import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import AppLayout from '../../../components/layout/AppLayout/AppLayout.jsx';
import SupplierTable from '../components/tables/SupplierTable.jsx';
import SupplierForm from '../components/forms/SupplierForm.jsx';
import Modal from '../../../components/ui/Modal/Modal.jsx';
import { useSuppliers } from '../hooks/useSuppliers.js';

const SuppliersPage = React.memo(({ onLogout }) => {
    const { t } = useTranslation(['inventory', 'common']);
    const suppliersHook = useSuppliers({
        limitOptions: [5, 15, 30, 50] // Suppliers-specific pagination options
    });
    
    // Modal state
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [editingSupplier, setEditingSupplier] = useState(null);
    
    // Form state
    const [formLoading, setFormLoading] = useState(false);
    
    // Stable callback functions
    const handleAddSupplier = useCallback(() => {
        setEditingSupplier(null);
        setShowCreateForm(true);
    }, []);
    
    const handleEditSupplier = useCallback((supplier) => {
        setEditingSupplier(supplier);
        setShowCreateForm(true);
    }, []);
    
    const handleFormClose = useCallback(() => {
        setShowCreateForm(false);
        setEditingSupplier(null);
    }, []);


    const handleSupplierSubmit = useCallback(async (supplierData) => {
        setFormLoading(true);
        suppliersHook.setError('');

        try {
            let result;
            if (editingSupplier) {
                result = await suppliersHook.updateSupplier(editingSupplier.id, supplierData);
            } else {
                result = await suppliersHook.createSupplier(supplierData);
            }

            if (result.success) {
                handleFormClose();
                suppliersHook.setError('');
            } else {
                suppliersHook.setError(result.error);
            }
        } catch (error) {
            console.error('Form submission error:', error);
            suppliersHook.setError('An unexpected error occurred. Please try again.');
        } finally {
            setFormLoading(false);
        }
    }, [editingSupplier, suppliersHook, handleFormClose]);

    // Show loading state only for initial load
    if (suppliersHook.loading && (!suppliersHook.suppliers || suppliersHook.suppliers.length === 0)) {
        return (
            <AppLayout 
                title={t('inventory:suppliers')}
                error={suppliersHook.error}
                setError={suppliersHook.setError}
                onLogout={onLogout}
            >
                <div className="inventory-container">
                    <div className="loading-state">
                        <div className="spinner"></div>
                        <p>{t('common:messages.loading')}</p>
                    </div>
                </div>
            </AppLayout>
        );
    }

    return (
        <AppLayout 
            title={t('inventory:suppliers')}
            error={suppliersHook.error}
            setError={suppliersHook.setError}
            onLogout={onLogout}
        >
            {/* Optimized SupplierTable handles all table logic */}
            <SupplierTable
                hookData={suppliersHook}
                onAddSupplier={handleAddSupplier}
                onEditSupplier={handleEditSupplier}
            />

            {/* Supplier Create/Edit Modal */}
            <Modal
                isOpen={showCreateForm}
                onClose={handleFormClose}
                title={editingSupplier ? t('inventory:editSupplier') : t('inventory:addSupplier')}
                footer={
                    <>
                        <button 
                            type="button" 
                            onClick={handleFormClose} 
                            className="btn btn-secondary"
                            disabled={formLoading}
                        >
                            {t('common:buttons.cancel')}
                        </button>
                        <button 
                            type="submit" 
                            form="supplier-form"
                            className="btn btn-primary"
                            disabled={formLoading}
                        >
                            {formLoading ? (
                                <>
                                    <span className="spinner-small"></span>
                                    {editingSupplier ? t('inventory:updatingSupplier') : t('inventory:creatingSupplier')}
                                </>
                            ) : (
                                editingSupplier ? t('inventory:updateSupplier') : t('inventory:createSupplier')
                            )}
                        </button>
                    </>
                }
            >
                <SupplierForm
                    supplier={editingSupplier}
                    onSubmit={handleSupplierSubmit}
                    loading={formLoading}
                />
            </Modal>

            {/* Loading overlay for actions */}
            {suppliersHook.actionLoading && (
                <div className="action-loading-overlay">
                    <div className="spinner"></div>
                </div>
            )}
        </AppLayout>
    );
});

export default SuppliersPage;